/*******************************************************************
* PAGE        : TextFieldComponent
* REMARKS     : Comman TextField Component
* CREATED ON  : 03-11-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 18-03-2026
********************************************************************/

import React, { useEffect, useRef, useState } from 'react';
import './TextFieldComponent.css';
import { useTranslation } from 'react-i18next';
import { Images } from '../../utils/Images';


const TextFieldComponent = ({
    label,
    value,
    onChange,
    error,
    type = 'text',
    alphanumericOnly = false,
    allowNegative = false,   // allow - new prop
    allowDecimal = false,    // allow . (optional future-proofing)

    decimalScale = 3,      // digits after decimal
    maxIntegerDigits = 18,  // digits before decimal

    icon,
    multiline = false,
    rows = 4,
    maxLength,
    style = {},
    min = 0,
    max = 100,
    fontFamily = 'Poppins',
    disabled = false,
    onEnterPress,
    enableEnter = false,
    onFocus,
    onBlur,
    placeholder,
    phoneNumber = false,
    onKeyDown,
    ...props
}) => {
    //#region
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const inputRef = useRef(null);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const MAX_ROWS = rows || 5;
    const MIN_ROWS = 1;

    const handleInput = (e) => {
        const textarea = e.target;

        textarea.rows = MIN_ROWS;

        const lineHeight = 24; // adjust if your font size differs
        const currentRows = Math.floor(textarea.scrollHeight / lineHeight);

        if (currentRows >= MAX_ROWS) {
            textarea.rows = MAX_ROWS;
            textarea.style.overflowY = 'auto';
        } else {
            textarea.rows = currentRows;
            textarea.style.overflowY = 'hidden';
        }

        handleValueChange(e);
    };

    useEffect(() => {
        if (multiline && inputRef.current) {
            const textarea = inputRef.current;

            textarea.rows = MIN_ROWS;

            const lineHeight = 24;
            const currentRows = Math.floor(textarea.scrollHeight / lineHeight);

            if (currentRows >= MAX_ROWS) {
                textarea.rows = MAX_ROWS;
                textarea.style.overflowY = 'auto';
            } else {
                textarea.rows = currentRows;
                textarea.style.overflowY = 'hidden';
            }
        }
    }, [value]);

    // const handleValueChange = (e) => {
    //     let inputValue = e.target.value;
    //     if (type === 'number') {
    //         if (/^\+?\d*$/.test(value)) {
    //             // Optional: Limit length (e.g., 15 digits max)
    //             if (inputValue.length <= maxLength) { // + sign + 15 digits
    //                 onChange && onChange(e);
    //             }
    //         }
    //     } else {
    //         onChange && onChange(e);
    //     }
    // };

    const handleValueChange = (e) => {
        let inputValue = e.target.value;

        // Allow only A-Z a-z 0-9
        if (alphanumericOnly) {
            const alphaNumRegex = /^[A-Za-z0-9]*$/;

            if (alphaNumRegex.test(inputValue)) {
                onChange && onChange(e);
            }
            return;
        }

        if (type === 'number') {

            // if (phoneNumber) {

            //     if (!/^\+?\d*$/.test(inputValue))
            //         return;

            //     if ((inputValue.match(/\+/g) || []).length > 1)
            //         return;

            //     if (inputValue.startsWith('+')) {
            //         if (inputValue.length > 13) return;
            //     }
            //     else if (inputValue.startsWith('0')) {
            //         if (inputValue.length > 11) return;
            //     }
            //     else {
            //         if (inputValue.length > 10) return;
            //     }

            //     onChange?.(e);
            //     return;
            // }

            if (phoneNumber) {
                // Allow digits, +, space, -, /, comma, (), #, x
                const phoneRegex = /^[0-9+\-(),/ #xX]*$/;

                if (!phoneRegex.test(inputValue)) {
                    return;
                }

                // Only one '+' and only at the beginning
                const plusCount = (inputValue.match(/\+/g) || []).length;

                if (plusCount > 1) {
                    return;
                }

                if (plusCount === 1 && !inputValue.startsWith("+")) {
                    return;
                }

                // Optional overall length limit
                if (inputValue.length > 50) {
                    return;
                }

                onChange?.(e);
                return;
            }

            let regex = '^';

            if (allowNegative) regex += '-?';

            if (allowDecimal) {
                regex += `\\d{0,${maxIntegerDigits}}(\\.\\d{0,${decimalScale}})?`;
            } else {
                regex += '\\d*';
            }

            regex += '$';

            const numberRegex = new RegExp(regex);

            // blocks: 10-10, --10, 1-0, 1.2.3, -1-1
            // allows: -, -10, 10, 0, -0, 10.5, -10.5
            if (numberRegex.test(inputValue)) {
                const pureNumber = inputValue.replace('-', '').replace('.', '');

                if (!maxLength || pureNumber.length <= maxLength) {
                    onChange && onChange(e);
                }
            }
        } else {
            onChange && onChange(e);
        }
    };

    const handleKeyDown = (e) => {
        onKeyDown?.(e);

        if (e.defaultPrevented) return;

        if (e.key === 'Enter') {
            if (onEnterPress) {
                onEnterPress(e.target.value);   // send value directly
            } else {
                e.preventDefault();
            }
        }
    };

    const handleFocus = (e) => {
        setFocused(true);
        onFocus?.(e);   // call parent if exists
    };

    const handleBlur = (e) => {
        setFocused(false);
        onBlur?.(e);    // call parent if exists
    };

    // const inputType = type === 'password' && showPassword ? 'text' : type;
    const inputType = type === 'number' ? 'text' : (type === 'password' && showPassword ? 'text' : type);

    const [focused, setFocused] = useState(false);

    //#endregion
    return (
        <div className="custom-input-group" style={style}>
            <label className="custom-input-label">{label}</label>
            {multiline ? (
                <div className={`custom-input-wrapper text-field-wrapper ${focused ? 'focused' : ''} ${error ? 'error' : ''}  ${value ? 'has-value' : ''}  ${icon ? 'has-icon' : ''} `}>
                    <textarea
                        placeholder={placeholder}
                        ref={inputRef}
                        disabled={disabled}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => e.preventDefault()}
                        value={value}
                        onChange={handleInput}
                        maxLength={maxLength}
                        rows={1}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        className={`custom-input-textarea text-field-textarea ${error ? 'error' : ''}`}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        name={`field_${Math.random().toString(36).substring(2, 15)}`}
                        style={{ cursor: disabled ? 'not-allowed' : 'text' }}
                        {...props}
                    />
                </div>
            ) : (
                <div className={`custom-input-wrapper text-field-wrapper ${focused ? 'focused' : ''} ${error ? 'error' : ''}  ${value ? 'has-value' : ''}  ${icon ? 'has-icon' : ''} `}>
                    <input
                        disabled={disabled}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => e.preventDefault()}
                        placeholder={placeholder}
                        type={inputType}
                        inputMode={type === 'number' ? 'numeric' : undefined}
                        pattern={type === 'number' ? '[0-9]*' : undefined}
                        value={value}
                        onChange={handleValueChange}
                        maxLength={maxLength}
                        min={min}
                        max={max}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        name={`field_${Math.random().toString(36).substring(2, 15)}`}
                        className={`custom-input text-field-input ${error ? 'error' : ''} ${icon ? 'has-icon' : ''}`}
                        style={{ ...style, paddingLeft: '8px', cursor: disabled ? 'not-allowed' : 'text', }}
                        {...props}
                    />

                    {icon && (<div className="search-icon"> {icon} </div>)}

                    {type === 'password' && (<div onClick={handleClickShowPassword} className="password-toggle">
                        {showPassword
                            ? <img src={Images.viewslash} alt='common_lable_hide' />
                            : <img src={Images.view} alt='common_lable_hide' />}
                    </div>)}
                </div>
            )}
            {error && <span className="error-text">{error}</span>}
        </div>
    );
};

export default TextFieldComponent;
